import React, { useActionState, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import Button from "../../components/button/Button.tsx";
import Input from "../../components/form-item/Input.tsx";
import IconButton from "../../components/button/IconButton.tsx";
import ToolTip from "../../components/tooltip/ToolTip.tsx";
import SvgLoadingAnimation from "../../components/loading/index.tsx";
import {
  useValidateResetTokenMutation,
  useConfirmPasswordResetMutation,
} from "../../app/services/passwordReset";
import Modal from "../../components/modal/Modal";
import { AuthErrorMessage } from "../../components/layouts/authStyles";

const ResetLink = styled(Link)`
  display: block;
  text-align: center;
  color: var(--soremo-light-gray);
  text-decoration: none;
  font-size: 13px;
  &:hover {
    color: var(--soremo-blue);
  }
`;

const ONLY_DIGITS = /^\d+$/;

type FormState = {
  error: string;
  fieldErrors: Record<string, string>;
};

const PasswordResetConfirm: React.FC = () => {
  const { uid, token } = useParams<{ uid: string; token: string }>();
  const navigate = useNavigate();

  const [validateToken] = useValidateResetTokenMutation();
  const [confirmReset] = useConfirmPasswordResetMutation();

  const [tokenStatus, setTokenStatus] = useState<
    "loading" | "valid" | "invalid"
  >("loading");

  useEffect(() => {
    if (!uid || !token) {
      setTokenStatus("invalid");
      return;
    }

    validateToken({ uid, token })
      .then((result) => {
        if (result.data) {
          setTokenStatus("valid");
        } else {
          setTokenStatus("invalid");
        }
      })
      .catch(() => {
        setTokenStatus("invalid");
      });
  }, [uid, token, validateToken]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      e.currentTarget.requestSubmit();
    }
  };

  const [state, formAction, isPending] = useActionState(
    async (_prevState: FormState, formData: FormData) => {
      const password = formData.get("password") as string;
      const passwordConfirm = formData.get("password_confirm") as string;
      const fieldErrors: Record<string, string> = {};

      if (!password) {
        fieldErrors.password = "パスワードを入力してください。";
      } else if (password.length < 8) {
        fieldErrors.password =
          "パスワードは8文字以上で入力してください。";
      } else if (ONLY_DIGITS.test(password)) {
        fieldErrors.password =
          "数字のみのパスワードは使用できません。";
      }

      if (!passwordConfirm) {
        fieldErrors.password_confirm =
          "確認用パスワードを入力してください。";
      } else if (password !== passwordConfirm) {
        fieldErrors.password_confirm = "パスワードが一致しません。";
      }

      if (Object.keys(fieldErrors).length > 0) {
        return { error: "", fieldErrors };
      }

      try {
        const result = await confirmReset({
          uid: uid!,
          token: token!,
          password,
          password_confirm: passwordConfirm,
        });

        if (result.data?.token) {
          navigate("/projects");
          return { error: "", fieldErrors: {} };
        }

        if (result.error) {
          const errorData = (result.error as any)?.data;
          if (errorData?.errors) {
            const serverFieldErrors: Record<string, string> = {};
            for (const [key, messages] of Object.entries(errorData.errors)) {
              serverFieldErrors[key] = (messages as string[])[0];
            }
            return {
              error: errorData.message || "",
              fieldErrors: serverFieldErrors,
            };
          }
          if (errorData?.message) {
            return { error: errorData.message, fieldErrors: {} };
          }
          return {
            error: "パスワードのリセットに失敗しました。再度お試しください。",
            fieldErrors: {},
          };
        }
      } catch {
        return {
          error:
            "ネットワークエラーが発生しました。インターネット接続を確認してください。",
          fieldErrors: {},
        };
      }

      return { error: "", fieldErrors: {} };
    },
    { error: "", fieldErrors: {} },
  );

  if (tokenStatus === "loading") {
    return <SvgLoadingAnimation />;
  }

  if (tokenStatus === "invalid") {
    return (
      <Modal variant="auth">
        <div className="u-col u-gap32">
          <ToolTip description="サインインに戻る" placement="right">
            <IconButton onClick={() => navigate("/signin")} icon="chevron_left" />
          </ToolTip>
          <AuthErrorMessage>
            リセットリンクが無効または期限切れです。再度お試しください。
          </AuthErrorMessage>
          <ResetLink to="/password-reset">
            再度リセットを申請する
          </ResetLink>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <Modal variant="auth">
        <div className="u-col u-gap32">
          <ToolTip description="サインインに戻る" placement="right">
            <IconButton onClick={() => navigate("/signin")} icon="chevron_left" />
          </ToolTip>

          <form action={formAction} onKeyDown={handleKeyDown}>
            <div className="u-col u-gap32">
              {state?.error && <AuthErrorMessage>{state.error}</AuthErrorMessage>}

              <div className="u-w100 u-col u-gap16">
                {/* パスワードマネージャーが保存済みの資格情報を正しく更新できる
                    よう new-password を明示する。未指定だとサインイン画面で
                    旧パスワードが補完され続け、再設定が反映されないように見える。 */}
                <Input
                  label=""
                  type="password"
                  placeholder="新しいパスワード"
                  name="password"
                  option="required"
                  autocomplete="new-password"
                  error={state?.fieldErrors?.password}
                />
                <Input
                  label=""
                  type="password"
                  placeholder="新しいパスワード（確認）"
                  name="password_confirm"
                  option="required"
                  autocomplete="new-password"
                  error={state?.fieldErrors?.password_confirm}
                />
              </div>
              <Button
                type="submit"
                theme="primary"
                variant="filled"
                size="large"
                width="full"
                rightIcon="chevron_right"
                disabled={isPending}
              >
                {isPending ? "設定中..." : "パスワードを再設定"}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
      {isPending && <SvgLoadingAnimation />}
    </>
  );
};

export default PasswordResetConfirm;
